import {
  BUSINESS_TYPE_LABELS,
  BUSINESS_TYPES,
  type BusinessTypeValue,
  formatCurrency,
} from '@crm/shared';
import { cn, Input, Modal, Select, Spinner } from '@crm/ui';
import { CircleDollarSign, Clock3, Eye, KanbanSquare, Rocket } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  EmptyState,
  KanbanBoard,
  KanbanCard,
  KanbanColumn,
  PageHeader,
  TabPages,
} from '@/components';
import { useDocumentTitle } from '@/hooks';
import { useSaasPlans } from '@/modules/saas/api';
import {
  BOARD_ROUTES,
  type BoardCard,
  type BoardKind,
  type BoardSignupStep,
  useBoard,
  useMoveCard,
  useSignupLog,
} from '../api';

const STEP_OPTIONS: { value: BoardSignupStep; label: string }[] = [
  { value: 'LEAD', label: 'Nome + WhatsApp' },
  { value: 'WHATSAPP_VERIFIED', label: 'Confirmação WhatsApp' },
  { value: 'ACCOUNT', label: 'Conta' },
  { value: 'COMPANY_DATA', label: 'Dados' },
  { value: 'SERVICES', label: 'Serviços' },
  { value: 'PROFESSIONALS', label: 'Profissionais' },
  { value: 'LANDING_PAGE', label: 'Landing Page' },
  { value: 'WHATSAPP_CONNECTED', label: 'WhatsApp' },
  { value: 'CAMPAIGN_STARTED', label: 'Campanha' },
  { value: 'DONE', label: 'Jornada concluída' },
];

/** Espelha colunaDaJornada() da API: em andamento, so falta o sistema fechar, ou concluida. */
const COLUMN_STEPS: Record<string, BoardSignupStep[]> = {
  'Novo Lead': [
    'LEAD',
    'WHATSAPP_VERIFIED',
    'ACCOUNT',
    'COMPANY_DATA',
    'SERVICES',
    'PROFESSIONALS',
    'LANDING_PAGE',
  ],
  'Finalizando Onboarding': ['WHATSAPP_CONNECTED', 'CAMPAIGN_STARTED'],
  Finalizado: ['DONE'],
};

type Periodo = 'ALL' | '30D' | '60D' | '90D' | '6M' | '1Y' | 'CUSTOM';

function periodoRange(periodo: Periodo, dataInicial: string, dataFinal: string) {
  if (periodo === 'ALL') return { createdFrom: undefined, createdTo: undefined };

  if (periodo === 'CUSTOM' && dataInicial && dataFinal) {
    return {
      createdFrom: new Date(`${dataInicial}T00:00:00`).toISOString(),
      createdTo: new Date(`${dataFinal}T23:59:59.999`).toISOString(),
    };
  }

  if (periodo === 'CUSTOM') return { createdFrom: undefined, createdTo: undefined };

  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const start = new Date(end);
  const days = { '30D': 29, '60D': 59, '90D': 89 }[periodo as '30D' | '60D' | '90D'];

  if (days !== undefined) start.setDate(start.getDate() - days);
  if (periodo === '6M') start.setMonth(start.getMonth() - 6);
  if (periodo === '1Y') start.setFullYear(start.getFullYear() - 1);
  start.setHours(0, 0, 0, 0);

  return { createdFrom: start.toISOString(), createdTo: end.toISOString() };
}

function ColumnInfoModal({ column, onClose }: { column: string; onClose: () => void }) {
  const steps = COLUMN_STEPS[column] ?? [];
  const labels = steps.map(
    (step) => STEP_OPTIONS.find((option) => option.value === step)?.label ?? step,
  );

  return (
    <Modal
      open
      title={`Steps de ${column}`}
      onClose={onClose}
      actions={[{ label: 'Fechar', onClick: onClose }]}
    >
      {labels.length > 0 ? (
        <ul className="space-y-2 text-sm">
          {labels.map((label) => (
            <li key={label} className="rounded-md bg-muted/40 px-3 py-2">
              {label}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">
          Esta coluna é movimentada manualmente e pode receber qualquer cadastro.
        </p>
      )}
    </Modal>
  );
}

function Cartao({
  card,
  columns,
  onDragStart,
  onMove,
  onOpen,
}: {
  card: BoardCard;
  columns: string[];
  onDragStart: () => void;
  onMove: (column: string) => void;
  onOpen?: () => void;
}) {
  return (
    <KanbanCard
      draggable
      onDragStart={onDragStart}
      className={card.late ? 'border-destructive/40 bg-destructive/5' : undefined}
    >
      {onOpen ? (
        <div>
          <p className="truncate text-sm font-medium">{card.companyName}</p>
          <div className="mt-2 flex min-w-0 items-center justify-between gap-2">
            <p className="shrink-0 text-xs font-medium text-primary">Plano {card.planName}</p>
            <button
              type="button"
              aria-label="Ver histórico do cadastro"
              title="Ver histórico do cadastro"
              className="flex min-w-0 items-center gap-1 border-0 bg-transparent p-0 text-right text-xs text-muted-foreground hover:text-foreground"
              onClick={onOpen}
            >
              <span className="truncate">{card.signupStatus}</span>
              <Eye className="h-4 w-4 shrink-0 text-primary" />
            </button>
          </div>
          {card.onWaitlist ? (
            <p className="flex items-center gap-1 text-xs text-amber-700 dark:text-amber-400">
              <Clock3 className="h-3 w-3" />
              Fila de Espera
            </p>
          ) : null}
          {card.detail ? (
            <p className={cn('text-xs', card.late ? 'text-destructive' : 'text-muted-foreground')}>
              {card.detail}
            </p>
          ) : null}
          {card.amount ? (
            <p className="text-sm font-semibold text-destructive">{formatCurrency(card.amount)}</p>
          ) : null}
        </div>
      ) : (
        <>
          <p className="truncate text-sm font-medium">{card.companyName}</p>
          <p className="text-xs font-medium text-primary">Plano {card.planName}</p>
          {card.detail ? <p className="text-xs text-muted-foreground">{card.detail}</p> : null}
        </>
      )}

      {/* No celular nao se arrasta card: mover tem de caber em um toque. */}
      <div className="flex justify-end">
        <Select
          aria-label={`Mover ${card.companyName}`}
          value={card.column}
          onChange={(event) => onMove(event.target.value)}
          className="h-8 w-auto min-w-0 border-0 bg-transparent px-1 text-right text-xs shadow-none focus-visible:ring-0"
        >
          {columns.map((column) => (
            <option key={column} value={column}>
              {column}
            </option>
          ))}
        </Select>
      </div>
    </KanbanCard>
  );
}

function SignupLogModal({ cardId, onClose }: { cardId: string; onClose: () => void }) {
  const log = useSignupLog(cardId);

  return (
    <Modal
      open
      title="Histórico do cadastro"
      subtitle={log.data?.companyName}
      onClose={onClose}
      actions={[{ label: 'Fechar', onClick: onClose }]}
    >
      {log.isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : log.error ? (
        <p className="text-sm text-destructive">Não foi possível carregar o histórico.</p>
      ) : log.data ? (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Status atual:{' '}
            <span className="font-medium text-foreground">{log.data.signupStatus}</span>
          </p>
          {(() => {
            const realizados = log.data.data.filter((entry) => entry.completedAt);
            const pendentes = log.data.data.filter((entry) => !entry.completedAt);

            return (
              <>
                <div>
                  <h3 className="mb-3 text-sm font-semibold">Steps realizados</h3>
                  {realizados.length > 0 ? (
                    <div className="relative ml-2 space-y-4 border-l border-primary/30 pl-5">
                      {realizados.map((entry) => (
                        <div key={entry.step} className="relative">
                          <span className="absolute -left-[1.62rem] top-1 h-2.5 w-2.5 rounded-full bg-primary ring-4 ring-background" />
                          <p className="text-sm font-medium">{entry.step}</p>
                          <p className="text-xs text-muted-foreground">
                            Iniciado em {entry.startedAt}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Concluído em {entry.completedAt}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">Nenhum step realizado.</p>
                  )}
                </div>

                <div className="mt-6">
                  <h3 className="mb-3 text-sm font-semibold">Steps pendentes</h3>
                  {pendentes.length > 0 ? (
                    <div className="space-y-3">
                      {pendentes.map((entry) => (
                        <div key={entry.step} className="rounded-lg bg-muted/40 p-3">
                          <p className="text-sm font-medium">{entry.step}</p>
                          <p className="text-xs text-muted-foreground">
                            Iniciado em {entry.startedAt}
                          </p>
                          <p className="text-xs text-muted-foreground">Em andamento</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">Nenhum step pendente.</p>
                  )}
                </div>
              </>
            );
          })()}
        </div>
      ) : null}
    </Modal>
  );
}

function Quadro({ board }: { board: BoardKind }) {
  const planos = useSaasPlans();
  const [planId, setPlanId] = useState('ALL');
  const [signupStep, setSignupStep] = useState<'ALL' | BoardSignupStep>('ALL');
  const [profileType, setProfileType] = useState<'ALL' | 'ESTABELECIMENTO' | 'AUTONOMO'>('ALL');
  const [businessType, setBusinessType] = useState<'ALL' | BusinessTypeValue>('ALL');
  const [nome, setNome] = useState('');
  const [periodo, setPeriodo] = useState<Periodo>('ALL');
  const [dataInicial, setDataInicial] = useState('');
  const [dataFinal, setDataFinal] = useState('');
  const filtroPlan = planId === 'ALL' ? undefined : planId;
  const filtroStep = signupStep === 'ALL' ? undefined : signupStep;
  const filtroProfileType = profileType === 'ALL' ? undefined : profileType;
  const filtroBusinessType =
    profileType === 'ESTABELECIMENTO' && businessType !== 'ALL' ? businessType : undefined;
  const filtroNome = nome.trim() || undefined;
  const periodoSelecionado = useMemo(
    () => periodoRange(periodo, dataInicial, dataFinal),
    [periodo, dataInicial, dataFinal],
  );
  const quadro = useBoard(
    board,
    filtroPlan,
    filtroStep,
    filtroProfileType,
    filtroBusinessType,
    filtroNome,
    periodoSelecionado.createdFrom,
    periodoSelecionado.createdTo,
  );
  const mover = useMoveCard(
    board,
    filtroPlan,
    filtroStep,
    filtroProfileType,
    filtroBusinessType,
    filtroNome,
    periodoSelecionado.createdFrom,
    periodoSelecionado.createdTo,
  );
  const [arrastando, setArrastando] = useState<string | null>(null);
  const [cardSelecionado, setCardSelecionado] = useState<string | null>(null);
  const [colunaInfo, setColunaInfo] = useState<string | null>(null);

  if (quadro.isLoading || !quadro.data) {
    return <EmptyState title="Carregando o quadro..." />;
  }

  const { columns, data: todosCards } = quadro.data;
  const data = todosCards;

  const planoFilter = (
    <div className="flex shrink-0 items-center gap-2">
      <label className="whitespace-nowrap text-sm font-medium" htmlFor={`filtro-plano-${board}`}>
        Plano
      </label>
      <Select
        id={`filtro-plano-${board}`}
        value={planId}
        onChange={(event) => setPlanId(event.target.value)}
        className="h-8 w-28 min-w-0 border-0 bg-transparent px-1 text-xs shadow-none focus-visible:ring-0"
      >
        <option value="ALL">Todos</option>
        <option value="FREE">Free</option>
        {planos.data?.data
          .filter((plan) => plan.name.toLowerCase() !== 'free')
          .map((plan) => (
            <option key={plan.id} value={plan.id}>
              {plan.name}
            </option>
          ))}
      </Select>
    </div>
  );

  const nomeFilter = (
    <div className="relative w-36 shrink-0">
      <Input
        id={`filtro-nome-${board}`}
        aria-label="Nome"
        value={nome}
        onChange={(event) => setNome(event.target.value)}
        placeholder=" "
        className="peer h-8 rounded-none border-0 border-b border-input bg-transparent px-1 pb-0 pt-3 text-xs shadow-none focus-visible:border-primary focus-visible:ring-0"
      />
      <label
        htmlFor={`filtro-nome-${board}`}
        className={cn(
          'pointer-events-none absolute left-1 transition-all',
          nome.trim()
            ? 'top-0 text-[10px] text-muted-foreground'
            : 'top-1/2 -translate-y-1/2 text-xs text-muted-foreground',
        )}
      >
        Nome
      </label>
    </div>
  );

  const periodoFilter = (
    <div className="flex shrink-0 items-center gap-2">
      <label className="whitespace-nowrap text-sm font-medium" htmlFor={`filtro-periodo-${board}`}>
        Período
      </label>
      <Select
        id={`filtro-periodo-${board}`}
        value={periodo}
        onChange={(event) => setPeriodo(event.target.value as Periodo)}
        className="h-8 w-36 min-w-0 border-0 bg-transparent px-1 text-xs shadow-none focus-visible:ring-0"
      >
        <option value="ALL">Todos os períodos</option>
        <option value="30D">Últimos 30 dias</option>
        <option value="60D">Últimos 60 dias</option>
        <option value="90D">Últimos 90 dias</option>
        <option value="6M">Últimos 6 meses</option>
        <option value="1Y">Último 1 ano</option>
        <option value="CUSTOM">Período diverso</option>
      </Select>
    </div>
  );

  const customPeriodFilter =
    periodo === 'CUSTOM' ? (
      <>
        <div className="relative w-32 shrink-0">
          <Input
            id={`filtro-data-inicial-${board}`}
            type="date"
            aria-label="Data inicial"
            value={dataInicial}
            onChange={(event) => setDataInicial(event.target.value)}
            placeholder=" "
            className="peer h-8 rounded-none border-0 border-b border-input bg-transparent px-1 pb-0 pt-3 text-xs shadow-none focus-visible:border-primary focus-visible:ring-0"
          />
          <label
            htmlFor={`filtro-data-inicial-${board}`}
            className={cn(
              'pointer-events-none absolute left-1 transition-all',
              dataInicial
                ? 'top-0 text-[10px] text-muted-foreground'
                : 'top-1/2 -translate-y-1/2 text-xs text-muted-foreground',
            )}
          >
            Data inicial
          </label>
        </div>
        <div className="relative w-32 shrink-0">
          <Input
            id={`filtro-data-final-${board}`}
            type="date"
            aria-label="Data final"
            value={dataFinal}
            onChange={(event) => setDataFinal(event.target.value)}
            placeholder=" "
            className="peer h-8 rounded-none border-0 border-b border-input bg-transparent px-1 pb-0 pt-3 text-xs shadow-none focus-visible:border-primary focus-visible:ring-0"
          />
          <label
            htmlFor={`filtro-data-final-${board}`}
            className={cn(
              'pointer-events-none absolute left-1 transition-all',
              dataFinal
                ? 'top-0 text-[10px] text-muted-foreground'
                : 'top-1/2 -translate-y-1/2 text-xs text-muted-foreground',
            )}
          >
            Data final
          </label>
        </div>
      </>
    ) : null;

  function soltar(column: string) {
    if (arrastando) mover.mutate({ id: arrastando, column });
    setArrastando(null);
  }

  return (
    <div className="space-y-3">
      {cardSelecionado ? (
        <SignupLogModal cardId={cardSelecionado} onClose={() => setCardSelecionado(null)} />
      ) : null}
      {colunaInfo ? (
        <ColumnInfoModal column={colunaInfo} onClose={() => setColunaInfo(null)} />
      ) : null}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg bg-muted/30 px-3 py-2">
        {board === 'ONBOARDING' ? (
          <>
            <div className="flex shrink-0 items-center gap-2">
              <label
                className="whitespace-nowrap text-sm font-medium"
                htmlFor={`filtro-perfil-${board}`}
              >
                Perfil
              </label>
              <Select
                id={`filtro-perfil-${board}`}
                value={profileType}
                onChange={(event) => {
                  const value = event.target.value as 'ALL' | 'ESTABELECIMENTO' | 'AUTONOMO';
                  setProfileType(value);
                  if (value !== 'ESTABELECIMENTO') setBusinessType('ALL');
                }}
                className="h-8 w-28 min-w-0 border-0 bg-transparent px-1 text-xs shadow-none focus-visible:ring-0"
              >
                <option value="ALL">Todos</option>
                <option value="ESTABELECIMENTO">Estabelecimento</option>
                <option value="AUTONOMO">Profissional autônomo</option>
              </Select>
            </div>
            {planoFilter}
            {profileType === 'ESTABELECIMENTO' ? (
              <div className="flex shrink-0 items-center gap-2">
                <label
                  className="whitespace-nowrap text-sm font-medium"
                  htmlFor={`filtro-tipo-${board}`}
                >
                  Tipo
                </label>
                <Select
                  id={`filtro-tipo-${board}`}
                  value={businessType}
                  onChange={(event) =>
                    setBusinessType(event.target.value as 'ALL' | BusinessTypeValue)
                  }
                  className="h-8 w-28 min-w-0 border-0 bg-transparent px-1 text-xs shadow-none focus-visible:ring-0"
                >
                  <option value="ALL">Todos</option>
                  {BUSINESS_TYPES.filter((type) => type !== 'DENTAL_CLINIC').map((type) => (
                    <option key={type} value={type}>
                      {BUSINESS_TYPE_LABELS[type]}
                    </option>
                  ))}
                </Select>
              </div>
            ) : null}
            <div className="flex shrink-0 items-center gap-2">
              <label
                className="whitespace-nowrap text-sm font-medium"
                htmlFor={`filtro-step-${board}`}
              >
                Step
              </label>
              <Select
                id={`filtro-step-${board}`}
                value={signupStep}
                onChange={(event) => setSignupStep(event.target.value as 'ALL' | BoardSignupStep)}
                className="h-8 w-28 min-w-0 border-0 bg-transparent px-1 text-xs shadow-none focus-visible:ring-0"
              >
                <option value="ALL">Todos</option>
                {STEP_OPTIONS.map((step) => (
                  <option key={step.value} value={step.value}>
                    {step.label}
                  </option>
                ))}
              </Select>
            </div>
          </>
        ) : null}
        {board !== 'ONBOARDING' ? planoFilter : null}
        {nomeFilter}
        {periodoFilter}
        {customPeriodFilter}
      </div>

      <KanbanBoard>
        {columns.map((column) => {
          const cards = data.filter((card) => card.column === column);

          return (
            <KanbanColumn
              key={column}
              label={column}
              count={cards.length}
              onInfo={() => setColunaInfo(column)}
              highlighted={Boolean(arrastando)}
              onDragOver={(event) => event.preventDefault()}
              onDrop={() => soltar(column)}
            >
              <div className={cn('space-y-2', mover.isPending && 'opacity-60')}>
                {cards.map((card) => (
                  <Cartao
                    key={card.id}
                    card={card}
                    columns={columns}
                    onDragStart={() => setArrastando(card.id)}
                    onMove={(proxima) => mover.mutate({ id: card.id, column: proxima })}
                    onOpen={board === 'ONBOARDING' ? () => setCardSelecionado(card.id) : undefined}
                  />
                ))}
              </div>
            </KanbanColumn>
          );
        })}
      </KanbanBoard>
    </div>
  );
}

/**
 * Os quadros de quem administra o SaaS. Cada aba busca ao abrir e cria o
 * cartao de quem ainda nao tem um - cartao ja movido fica onde esta.
 */
export function BoardPage() {
  useDocumentTitle('Board');

  return (
    <div className="space-y-4">
      <PageHeader
        icon={<KanbanSquare />}
        title="Board"
        description="Implantacao e cobranca dos assinantes, em quadros."
      />

      <TabPages
        label="Aba do board"
        basePath={BOARD_ROUTES.BOARD}
        pages={[
          {
            key: 'onboarding',
            label: 'Onboarding',
            icon: Rocket,
            render: () => <Quadro board="ONBOARDING" />,
          },
          {
            key: 'cobranca',
            label: 'Cobranca',
            icon: CircleDollarSign,
            render: () => <Quadro board="BILLING" />,
          },
        ]}
      />
    </div>
  );
}
